// src/services/eventsServices.js

import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/events`;

const getToken = () => localStorage.getItem("token");

const getAuthHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
});

/// GET
// Récupérer tous les événements
export const getAllEvents = async () => {
  try {
    const response = await axios.get(`${apiUrl}/allEvents`, {
      headers: getAuthHeaders(),
    });

    if (response.status !== 200) {
      throw new Error("Failed to fetch events");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des événements :", error);
    return [];
  }
};

// Récupérer uniquement les événements actifs
export const getActiveEvents = async () => {
  try {
    const response = await axios.get(`${apiUrl}/activeEvents`);

    if (response.status !== 200) {
      throw new Error("Failed to fetch active events");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des événements actifs :", error);
    return [];
  }
};

// Récupérer un événement par son id
export const getEventById = async (eventId) => {
  try {
    const response = await axios.get(`${apiUrl}/event/${eventId}`, {
      headers: getAuthHeaders(),
    });

    if (response.status !== 200) {
      throw new Error("Failed to fetch event");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement de l'événement :", error);
    return null;
  }
};

// Récupérer les inscriptions d’un événement
export const getEventRegistrations = async (eventId) => {
  try {
    const response = await axios.get(`${apiUrl}/${eventId}/registrations`, {
      headers: getAuthHeaders(),
    });

    if (response.status !== 200) {
      throw new Error("Failed to fetch registrations");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des inscriptions :", error);
    return {
      event_id: eventId,
      registrations_count: 0,
      registrations: [],
    };
  }
};

/// POST
// Ajouter un événement
export const addEvent = async (newEvent) => {
  try {
    const response = await axios.post(`${apiUrl}/addEvent`, newEvent, {
      headers: getAuthHeaders(),
    });

    if (response.status !== 200 && response.status !== 201) {
      throw new Error("Failed to add event");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'ajout d'un événement :", error);
    return null;
  }
};

// Upload image pour la catégorie events
export const uploadEventImage = async (formData) => {
  try {
    const response = await axios.post(`${apiUrl}/uploadImage`, formData, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
        "x-category": "events",
      },
    });

    if (response.status !== 200) {
      throw new Error("Failed to upload event image");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'upload de l'image événement :", error);
    return null;
  }
};

// Inscrire un visiteur / adhérent à un événement
export const registerVisitorToEvent = async (eventId, visitorData) => {
  try {
    const response = await axios.post(`${apiUrl}/${eventId}/register`, visitorData);

    if (response.status !== 200 && response.status !== 201) {
      throw new Error("Failed to register visitor");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'inscription à l'événement :", error);
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "Erreur lors de l'inscription à l'événement.",
    };
  }
};

/// PUT
// Modifier un événement
export const updateEvent = async (event) => {
  try {
    const response = await axios.put(`${apiUrl}/updateEvent/${event.id}`, event, {
      headers: getAuthHeaders(),
    });

    if (response.status !== 200) {
      throw new Error("Failed to update event");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la modification de l'événement :", error);
    return null;
  }
};

// Modifier une inscription visiteur
export const updateEventRegistration = async (
  eventId,
  registrationId,
  registrationData
) => {
  try {
    const response = await axios.put(
      `${apiUrl}/${eventId}/registrations/${registrationId}`,
      registrationData,
      {
        headers: getAuthHeaders(),
      }
    );

    if (response.status !== 200) {
      throw new Error("Failed to update registration");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la modification de l'inscription :", error);
    return null;
  }
};

/// PATCH
// Modifier la photo d’un événement
export const updateEventPhoto = async (eventId, file) => {
  const formData = new FormData();
  formData.append("image", file);

  try {
    const response = await axios.patch(`${apiUrl}/${eventId}/photo`, formData, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
        "x-category": "events",
      },
    });

    if (response.status !== 200) {
      throw new Error("Failed to update event photo");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la modification de la photo :", error);
    return null;
  }
};

// Activer / désactiver un événement
export const toggleEventActive = async (eventId) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/${eventId}/toggleActive`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );

    if (response.status !== 200) {
      throw new Error("Failed to toggle event active");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors du changement de statut de l'événement :", error);
    return null;
  }
};

// Ouvrir / fermer les inscriptions
export const toggleEventRegister = async (eventId) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/${eventId}/toggleRegister`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );

    if (response.status !== 200) {
      throw new Error("Failed to toggle event register");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors du changement d'état des inscriptions :", error);
    return null;
  }
};

/// DELETE
// Supprimer un événement
export const deleteEvent = async (eventId) => {
  try {
    const response = await axios.delete(`${apiUrl}/deleteEvent/${eventId}`, {
      headers: getAuthHeaders(),
    });

    if (response.status !== 200) {
      throw new Error("Failed to delete event");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression de l'événement :", error);
    return null;
  }
};

// Supprimer une inscription visiteur
export const deleteEventRegistration = async (eventId, registrationId) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/${eventId}/registrations/${registrationId}`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (response.status !== 200) {
      throw new Error("Failed to delete registration");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression de l'inscription :", error);
    return null;
  }
};